import React, { useEffect, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, RotateCw } from "lucide-react";
import { PaginationData, UserDailyTokenData } from "../../../../types";
import { getTokenUsageForPaginatedUsers } from "../../../../service/apiService";

const pad = (n: number) => String(n).padStart(2, "0");
const fmtUTC = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

// "Today" as a UTC midnight Date — the backend stores usage/cost dates in UTC,
// so the report's day boundaries follow UTC too.
const getTodayUTC = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
};

// Default window: the last 7 days (today and the 6 preceding days, UTC).
const getDefaultRange = () => {
  const end = getTodayUTC();
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 6);
  return { start: fmtUTC(start), end: fmtUTC(end) };
};

// First day of the current month and today, used to bound the day picker.
const getMonthBounds = () => {
  const today = getTodayUTC();
  const firstOfMonth = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)
  );
  return { firstOfMonth: fmtUTC(firstOfMonth), today: fmtUTC(today) };
};

// Every date in [start, end] (inclusive), most recent first. Guarded against a
// runaway loop on bad input.
const getDatesInRange = (start: string, end: string): string[] => {
  const dates: string[] = [];
  const cur = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  if (isNaN(cur.getTime()) || isNaN(last.getTime()) || cur > last) return dates;
  let guard = 0;
  while (cur <= last && guard < 366) {
    dates.push(fmtUTC(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
    guard += 1;
  }
  return dates.reverse();
};

// Usage now arrives as one row per feature and model, so a user can have several
// rows for the same day. Summing them is required: taking the first match (as
// this did) would silently report one feature's tokens as the whole day.
const totalsFor = (
  usage: { date: string; inputTokens: number; outputTokens: number }[],
  date: string
) =>
  usage.reduce(
    (acc, row) =>
      row.date === date
        ? {
            inputTokens: acc.inputTokens + (row.inputTokens || 0),
            outputTokens: acc.outputTokens + (row.outputTokens || 0),
          }
        : acc,
    { inputTokens: 0, outputTokens: 0 }
  );

const formatCompact = (n: number) => {
  const value = n || 0;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return String(value);
};

const formatMoney = (amount: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount || 0);
  } catch {
    return `${(amount || 0).toFixed(4)} ${currency}`;
  }
};

const DailyTokenUsageTable = () => {
  const defaultRange = getDefaultRange();
  const monthBounds = getMonthBounds();

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [userData, setUserData] = useState<UserDailyTokenData[]>([]);
  const [dailyCosts, setDailyCosts] = useState<Record<string, number>>({});
  const [costCurrency, setCostCurrency] = useState("USD");
  // Account-wide tokens per day from CloudWatch: everything Bedrock metered that
  // day, including work no request of ours produced. Shown beside the per-user
  // rows so a day whose usage mostly isn't attributable is visible, not missing.
  const [accountTokens, setAccountTokens] = useState<
    Record<string, { input_tokens: number; output_tokens: number }>
  >({});
  const [pagination, setPagination] = useState<PaginationData | null>({
    page: 1,
    items_per_page: 10,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTokenUsage = async (page: number, itemsPerPage: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTokenUsageForPaginatedUsers(
        page,
        itemsPerPage,
        "daily",
        undefined,
        startDate,
        endDate
      );
      const formattedUserData: UserDailyTokenData[] = Object.entries(
        result.usage_data
      ).map(([email, usage]) => ({
        email,
        usage: (usage as any[]).map((item: any) => ({
          date: item.created_at.split(" ")[0], // Extract just the date part
          inputTokens: parseInt(item.input_tokens, 10),
          outputTokens: parseInt(item.output_tokens, 10),
        })),
      }));
      setUserData(formattedUserData);
      setPagination(result.pagination);
      setDailyCosts(result.metadata?.daily_costs || {});
      setCostCurrency(result.metadata?.cost_currency || "USD");
      setAccountTokens(result.metadata?.daily_account_tokens || {});
    } catch (error) {
      console.error("Error fetching token usage:", error);
      setError("Failed to fetch token usage data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pagination) {
      fetchTokenUsage(pagination.page, pagination.items_per_page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination?.page, pagination?.items_per_page, startDate, endDate]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => {
      if (prev === null) {
        return {
          page: newPage,
          items_per_page: 10,
          total_pages: 1,
          has_next: false,
          next_page: null,
        };
      }
      return { ...prev, page: newPage };
    });
  };

  const resetToLast7Days = () => {
    const def = getDefaultRange();
    setStartDate(def.start);
    setEndDate(def.end);
  };

  // Columns are every day in the selected range (so days with no usage still
  // show as 0), most recent first.
  const dates = getDatesInRange(startDate, endDate);

  const isDefaultRange =
    startDate === defaultRange.start && endDate === defaultRange.end;
  const rangeLabel = isDefaultRange
    ? "last 7 days"
    : `${startDate} → ${endDate}`;

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Token Usage Report ({rangeLabel})
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-4 text-sm text-gray-600">
        {/* Day filter — pick any range within the current month */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              From
            </label>
            <input
              type="date"
              value={startDate}
              min={monthBounds.firstOfMonth}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={monthBounds.today}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <button
            type="button"
            onClick={resetToLast7Days}
            disabled={isDefaultRange}
            className={`py-1.5 px-3 rounded-md border text-sm font-medium ${
              isDefaultRange
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-white text-violet-700 border-violet-300 hover:bg-violet-50"
            }`}
          >
            Last 7 days
          </button>
        </div>

        {pagination && (
          <div className="mb-4 flex justify-between items-center">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                pagination.page === 1
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
            >
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                !pagination.has_next
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
            >
              Next
            </button>
          </div>
        )}
        <div className="mt-6 overflow-auto rounded-lg border border-gray-200 max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div>Error: {error}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-20">
                <tr>
                  <th
                    className="sticky left-0 z-30 bg-gray-100 border-r border-gray-200 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    rowSpan={2}
                  >
                    User
                  </th>
                  {dates.map((date: string) => (
                    <th
                      key={date}
                      colSpan={2}
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <div>{date}</div>
                      {/* Total Bedrock LLM cost for the day (account-wide) */}
                      <div
                        className="mt-1 normal-case font-semibold text-violet-700"
                        title="Total Bedrock LLM cost for this day"
                      >
                        {dailyCosts[date] != null
                          ? formatMoney(dailyCosts[date], costCurrency)
                          : "—"}
                      </div>
                      {/* Account-wide tokens metered that day, for comparison
                          with the per-user rows below. */}
                      <div
                        className="mt-0.5 normal-case font-normal text-gray-400"
                        title="All Bedrock tokens metered in the AWS account this day, including usage not traceable to a user"
                      >
                        {accountTokens[date]
                          ? `${formatCompact(
                              accountTokens[date].input_tokens +
                                accountTokens[date].output_tokens
                            )} acct`
                          : "—"}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr>
                  {dates.map((date) => (
                    <React.Fragment key={date}>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Input
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Output
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userData.length > 0 ? (
                  userData.map((user) => (
                    <tr key={user.email}>
                      <td className="sticky left-0 z-10 bg-white border-r border-gray-200 px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email}
                      </td>
                      {dates.map((date) => {
                        const usageForDate = totalsFor(user.usage, date);
                        return (
                          <React.Fragment key={`${user.email}-${date}`}>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm tabular-nums text-gray-900">
                              {usageForDate.inputTokens.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm tabular-nums text-gray-900">
                              {usageForDate.outputTokens.toLocaleString()}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={dates.length * 2 + 1}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No token usage data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default DailyTokenUsageTable;
